import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import uniqid from "uniqid";
import ConfirmDelete from "../../common/ConfirmDelete";
import { minToHour, getDayDiffIndex } from "../../../functions/formatTime";
import BlackBg from "../../common/PopUp/BlackBg";
import PopUpHeader from "../../common/PopUp/PopUpHeader";
import ButtonGroup from "../../common/PopUp/ButtonGroup";
import { fetchWithToken } from "../../../functions/fetch";
import { useUpdateTimeSlot } from "../../../contexts/updateTimeSlotContext";

const ReservationPopUp = ({
  reservation,
  canDelete,
  setShowPopUp,
  isOverdue,
  timeSlots,
  setFunction,
  setRefetchReservation,
}) => {
  // const defaultTime = DateTime.fromISO(reservation.startTime).

  const purposeOptions = ["看房", "入住簽約"]; // index of the option is value sent to backend
  const itemName = "預約";

  const [reservationDate, setReservationDate] = useState("");
  const [timeOptions, setTimeOptions] = useState([]);
  const [startTime, setStartTime] = useState("08:00");
  const [purpose, setPurpose] = useState("0");
  const [note, setNote] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [showed, setShowed] = useState(false);
  const [errors, setErrors] = useState([]);

  const { setUpdateTimeSlot } = useUpdateTimeSlot();
  const { deleted } = reservation;

  // SECTION: fetch all available rooms
  useEffect(() => {
    fetchWithToken(
      "GET",
      `${process.env.REACT_APP_BACKEND_HOST}/api/room/reservable`,
    )
      .then((res) => {
        if (res.status === 200) {
          const roomOptions = [
            { roomNumber: reservation.roomNumber },
            ...res.rooms,
          ];
          setRooms(roomOptions);
          return true;
        } else {
          console.error(res);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  // SECTION: update default value
  useEffect(() => {
    // startTime
    const date = DateTime.fromISO(reservation?.startTime);
    const str = date.toFormat("HH:mm");
    setStartTime(str);

    // purpose
    if (reservation) {
      setPurpose(String(reservation.purpose));
    } else {
      setPurpose("0");
    }

    // reservation date
    if (reservation) {
      const date = DateTime.fromISO(reservation.startTime);
      setReservationDate(date.toFormat("yyyy-MM-dd"));
    } else {
      setReservationDate(DateTime.now().toFormat("yyyy-MM-dd"));
    }

    setNote(reservation?.note ?? "");

    setRoomNumber(reservation?.roomNumber ?? "無");

    setShowed(true);
  }, [reservation]);

  // calculate options for reservation time
  // set effects of time-slot
  useEffect(() => {
    const optionStartTime = DateTime.fromISO("08:00", { zone: "utc" });
    const optionEndTime = DateTime.fromISO("17:00", { zone: "utc" });
    const date = DateTime.fromISO(reservationDate);
    const options = [];

    // time of current date/hour
    const originDate = DateTime.fromISO(reservation.startTime, {
      zone: "Asia/Shanghai",
    });
    const originHour = originDate.hour + minToHour(originDate.minute);

    let currentTime = optionStartTime;

    while (currentTime < optionEndTime) {
      const nextTime = currentTime.plus({ minutes: 30 });
      const hour = currentTime.hour + minToHour(currentTime.minute);
      const index = getDayDiffIndex(date);
      // either valid or is time of current reservation
      const inTimeSlot =
        index < timeSlots.length && timeSlots[index].hours.includes(hour);

      // option current choosing and original date of editing reservation
      const originDt = DateTime.fromISO(reservation.startTime).startOf("day");
      const currentDt = DateTime.fromISO(reservationDate);

      // equal reservation date and hour is reservation hour
      const isOriginTime = originDt.equals(currentDt) && hour === originHour;

      if (inTimeSlot || isOriginTime) {
        options.push(
          `${currentTime.toFormat("HH:mm")} - ${nextTime.toFormat("HH:mm")}`,
        );
      }
      currentTime = nextTime;
    }

    setTimeOptions(options);

    // if last startTime exist in new options, use old one
    // else, use first one in new options
    const values = options.map((option) => getTimeOptionValue(option));

    if (showed && !values.includes(startTime)) {
      setStartTime(values[0]);
    }
  }, [reservationDate]);

  const getTimeOptionValue = (text) => {
    return text.split("-")[0].trim();
  };

  const onChangeStartTime = (event) => {
    setStartTime(event.target.value);
  };

  const onChangePurpose = (event) => {
    setPurpose(event.target.value);
  };

  const onChangeReservationDate = (event) => {
    setReservationDate(event.target.value);
  };

  const onChangeNote = (event) => {
    setNote(event.target.value);
  };

  const onChangeRoomNumber = (event) => {
    setRoomNumber(event.target.value);
  };

  const askConfirmDelete = () => {
    setConfirmingDelete(true);
  };

  const cancelDelete = () => {
    setConfirmingDelete(false);
  };

  const deleteReservation = async () => {
    const formBody = {
      reservationId: reservation._id,
    };

    try {
      const res = await fetchWithToken(
        "DELETE",
        `${process.env.REACT_APP_BACKEND_HOST}/api/reservation`,
        formBody,
      );
      if (res.status === 200) {
        setShowPopUp(false);
        setConfirmingDelete(false);
        setRefetchReservation(true);
        setUpdateTimeSlot(true);
      } else {
        // error
        console.error(`Error deleting reservation: ${res.status}:${res}`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateReservation = async () => {
    // create a DateTime object with the local date and time
    const dt = DateTime.fromFormat(
      `${reservationDate} ${startTime}`,
      "yyyy-MM-dd HH:mm",
    );

    // set the timezone to UTC+8
    const dtUtc8 = dt.setZone("Asia/Shanghai");

    // format the DateTime object as a string in the UTC+8 timezone
    const newStartTime = dtUtc8.toISO();

    const formBody = {
      reservationId: reservation._id,
      startTime: newStartTime,
      note,
      purpose,
      roomNumber,
    };

    try {
      const res = await fetchWithToken(
        "PUT",
        `${process.env.REACT_APP_BACKEND_HOST}/api/reservation`,
        formBody,
      );
      if (res.status === 200) {
        setShowPopUp(false);
        setRefetchReservation(true);
        setUpdateTimeSlot(true);
      } else {
        // for error message
        setErrors(res.errors);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cancel = () => {
    setShowPopUp(false);
    setConfirmingDelete(false);
  };

  // if isOverdue or overdue, only allow editing note
  const cantEdit = isOverdue || deleted;
  // if confirming delete, show confirm content, else show form
  return (
    <>
      <BlackBg cancel={cancel} />
      <div className="absolute m-auto inset-0 w-fit h-fit bg-white z-10">
        <PopUpHeader itemName={itemName} />
        {confirmingDelete ? (
          <ConfirmDelete
            itemName={itemName}
            deleteFunction={deleteReservation}
            cancelFunction={cancelDelete}
          />
        ) : (
          <div className="font-chinese grid grid-cols-[repeat(2,min-content_1fr)] gap-y-2 gap-x-2 p-4 whitespace-nowrap">
            <label className="justify-self-end" htmlFor="reservationDate">
              預約日期
            </label>
            <input
              id="reservationDate"
              type="date"
              value={reservationDate}
              onChange={onChangeReservationDate}
              className={`${cantEdit ? "bg-white" : "bg-input-gray pl-1"} mr-4`}
              disabled={cantEdit}
            />

            <label className="justify-self-end" htmlFor="startTime">
              時段
            </label>
            <select
              id="startTime"
              className={`${cantEdit ? "bg-white" : "bg-input-gray pl-1"} mr-4`}
              value={startTime}
              onChange={onChangeStartTime}
              disabled={cantEdit}
            >
              {timeOptions.map((text) => (
                <option
                  key={uniqid()}
                  className="bg-white"
                  value={getTimeOptionValue(text)}
                >
                  {text}
                </option>
              ))}
            </select>

            <p className="justify-self-end">姓名</p>
            <p>{reservation?.userId?.name ?? "無"}</p>

            <p className="justify-self-end">聯絡電話</p>
            <p>{reservation?.userId?.phoneNumber ?? "無"}</p>

            <label className="justify-self-end" htmlFor="purpose">
              目的
            </label>
            <select
              id="purpose"
              className={`${cantEdit ? "bg-white" : "bg-input-gray pl-1"} mr-4`}
              value={purpose}
              onChange={onChangePurpose}
              disabled={cantEdit}
            >
              {purposeOptions.map((text, index) => (
                <option
                  className="bg-white"
                  key={uniqid()}
                  value={String(index)}
                >
                  {text}
                </option>
              ))}
            </select>

            <label htmlFor="roomNum">預定房號</label>
            <select
              id="roomNum"
              type="text"
              className={`${cantEdit ? "bg-white" : "bg-input-gray pl-1"} mr-4`}
              value={roomNumber}
              onChange={onChangeRoomNumber}
              disabled={cantEdit}
            >
              {rooms.map((room) => (
                <option key={uniqid()} value={room.roomNumber}>
                  {room.roomNumber}
                </option>
              ))}
            </select>

            <p className="justify-self-end">使用者備註</p>
            <textarea
              className="col-span-3 resize-y bg-white  p-1 mr-4"
              disabled
              value={reservation.userNote === "" ? "無" : reservation.userNote}
            />

            <label className="justify-self-end" htmlFor="note">
              備註
            </label>
            <textarea
              id="note"
              className="bg-input-gray col-span-3 p-1 mr-4"
              value={note}
              onChange={onChangeNote}
            />
            <ButtonGroup
              errors={errors}
              canDelete={canDelete && !deleted}
              updateFunction={updateReservation}
              askConfirmDelete={askConfirmDelete}
              cancelFunction={cancel}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ReservationPopUp;
